<script setup>
// The two remove confirmations. Figma: 4669:56307 (Remove Saved Seat) and
// 4669:56416 (Remove From My Schedule).
//
// One component because the nodes differ only in which sentence leads. The
// important thing they share is the RULE: removing a saved seat and removing
// the session from My Schedule are the same operation on a limited-capacity
// session. Both nodes carry the identical confirm label — "Remove Saved Seat &
// From My Schedule" — and each notice says doing one does the other. So the
// caller decides which wording fits the button the attendee pressed, not which
// side effect happens.
//
// The flow annotations (4669:56554 / 4669:56555) add that this applies
// REGARDLESS of whether the session is full.
const props = defineProps({
    sessionTitle: { type: String, default: '' },
    // 'seat'     — they pressed Remove Saved Seat
    // 'schedule' — they pressed Remove From My Schedule
    // 'standby'  — they pressed Standby on a session they are WAITLISTED for.
    //              They hold no seat, so the seat wording is not merely off-tone,
    //              it is untrue: it asked them to confirm removing "your saved
    //              seat" for a seat they were never given.
    // 'schedule-waitlisted' — they pressed Remove From My Schedule while
    //              waitlisted. The 'schedule' wording promised to remove "your
    //              saved seat" here too, which they do not have; copy is Nick's,
    //              verbatim (Asana 1218521036751118).
    variant: {
        type: String,
        default: 'seat',
        validator: (v) => ['seat', 'schedule', 'standby', 'schedule-waitlisted'].includes(v),
    },
    // Whether this session is part of a clash, and whether removing it settles
    // the whole schedule (Nick, Asana 1217708959827768 / 827761 / 827745 /
    // 827738; Figma 4755:114255, 4755:112639, 4755:112786, 4755:112492).
    //
    //   'none'     not conflicting — the modal reads exactly as it always has
    //   'resolves' removing it leaves NO conflicts anywhere on My Schedule
    //   'remains'  the clash outlives this removal, so more is still to do
    //
    // 'remains' is not just different words: the comps swap the filled button
    // from Cancel to Learn More, because when the attendee is mid-way through
    // untangling several clashes the useful offer is to see them, not to back
    // out of the one they just chose.
    conflict: {
        type: String,
        default: 'none',
        validator: (v) => ['none', 'resolves', 'remains'].includes(v),
    },
});
const open = defineModel('open', { default: true });
const emit = defineEmits(['close']);

// Resolves to the opener via emit('close', value) — the overlay's promise takes
// whatever the close event carries, the same contract remove-feedback.vue uses.
// Returning nothing here would read as `undefined` at the call site and cancel
// every removal.
const result = ref(false);

const quoted = computed(() => `“${props.sessionTitle}”`);

const baseHeadline = computed(() => {
    if (props.variant === 'schedule' || props.variant === 'schedule-waitlisted') return `Are you sure you want to remove ${quoted.value} from My Schedule?`;
    if (props.variant === 'standby') return `Are you sure you want to leave the waitlist for ${quoted.value}?`;
    return `Are you sure you want to remove your saved seat for ${quoted.value}?`;
});

// The conflict outcome is appended rather than replacing the question — the
// comps keep the original sentence and add what happens after it.
const HEADLINE_SUFFIX = {
    resolves: 'This will resolve the issue!',
    remains: 'Additional action will still be needed to resolve the issue.',
};
const modalHeadline = computed(() => {
    const suffix = HEADLINE_SUFFIX[props.conflict];
    return suffix ? `${baseHeadline.value} ${suffix}` : baseHeadline.value;
});

// Same fact stated from whichever end the attendee started at.
const baseNotice = computed(() => {
    if (props.variant === 'schedule') return 'This session has a limited capacity and requires a saved seat in order to attend. Removing this session from My Schedule will also remove your saved seat.';
    // No promise about attending: a standby place is not a seat, and saying it
    // "requires a saved seat in order to attend" alongside "leave the waitlist" would read as though they were giving up something they had.
    if (props.variant === 'schedule-waitlisted') return 'This session has a limited capacity and requires a saved seat in order to attend. You are currently on the waitlist for available seats. Removing this session from My Schedule will also remove you from the waitlist.';
    if (props.variant === 'standby') return 'This session is full and you are on the waitlist. Leaving the waitlist will also remove this session from My Schedule, and you will lose your place in line.';
    return 'This session has a limited capacity and requires a saved seat in order to attend. Removing your saved seat will also remove this session from My Schedule.';
});

const NOTICE_SUFFIX = {
    resolves: 'There will be no more conflicts on My Schedule.',
    remains: 'There will still be other conflicting items on My Schedule that require action.',
};
const notice = computed(() => {
    const suffix = NOTICE_SUFFIX[props.conflict];
    return suffix ? `${baseNotice.value} ${suffix}` : baseNotice.value;
});

// Names both effects of the one operation, from the end the attendee started at.
const confirmLabel = computed(() => ({
    standby: 'Leave Waitlist & Remove From My Schedule',
    'schedule-waitlisted': 'Remove From My Schedule & The Waitlist',
}[props.variant] || 'Remove Saved Seat & From My Schedule'));

// Cancel gives way to Learn More only when the clash outlives the removal.
const showsLearnMore = computed(() => props.conflict === 'remains');

const handleCancel = () => {
    result.value = false;
    open.value = false;
};

// Resolves 'learn' rather than a boolean, so the opener can tell it apart from
// a plain cancel and show the conflict list instead of doing nothing.
const handleLearnMore = () => {
    result.value = 'learn';
    open.value = false;
};

const handleConfirm = () => {
    result.value = true;
    open.value = false;
};

// Fires once when the shell transitions open → closed, by any route (confirm,
// Cancel, ✕, Esc, backdrop). Anything other than Confirm leaves result false.
const onShellClose = () => emit('close', result.value);
</script>

<template>
    <FragmentsModalShell
        v-model:open="open"
        :title="modalHeadline"
        @close="onShellClose"
    >
        <div class="flex flex-col items-center gap-[45px] w-full">
            <h2 class="font-hubspot-serif font-normal text-[25px] sm:text-[30px] leading-[1.3] tracking-normal text-(--magenta) text-center m-0 w-full">{{ modalHeadline }}</h2>

            <div class="flex items-start gap-[14px] w-full text-left bg-white rounded-[10px] px-[28px] py-[25px]">
            <svg class="shrink-0 mt-[4px] size-[16px] text-(--magenta)" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <circle cx="8" cy="8" r="8" fill="currentColor" />
                <path d="M8 4V9" stroke="white" stroke-width="1.5" stroke-linecap="round" />
                <circle cx="8" cy="11.6" r="0.9" fill="white" />
            </svg>
            <p class="font-hubspot-sans font-light text-[15px] leading-[23px] tracking-[0.3px] text-(--magenta) m-0">
                {{ notice }}
            </p>
        </div>

            <!-- Cancel is the FILLED button and the destructive action is the
                 outlined one — the comp inverts the usual emphasis on purpose, so
                 the safe choice carries the weight. Preserved deliberately; this
                 is not the pattern the other modals in this flow use. -->
            <div class="flex flex-wrap items-start justify-center gap-[16px]">
                <!-- The filled slot. Cancel normally, but Learn More once the
                     clash outlives this removal — backing out helps nobody who is
                     part-way through untangling several. -->
                <button
                    v-if="showsLearnMore"
                    type="button"
                    data-test="seat-remove-learn-more"
                    class="inline-flex items-center rounded-[50px] px-[20px] py-[10px]
                        bg-(--magenta) border border-(--magenta) text-white
                        font-hubspot-serif text-[17px] leading-[1.4] tracking-[0.34px] cursor-pointer
                        transition-colors duration-200 hover:bg-(--purple) hover:border-(--purple)
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--magenta)"
                    @click="handleLearnMore"
                >Learn More</button>

                <button
                    v-else
                    type="button"
                    data-test="seat-remove-cancel"
                    class="inline-flex items-center rounded-[50px] px-[20px] py-[10px]
                        bg-(--magenta) border border-(--magenta) text-white
                        font-hubspot-serif text-[17px] leading-[1.4] tracking-[0.34px] cursor-pointer
                        transition-colors duration-200 hover:bg-(--purple) hover:border-(--purple)
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--magenta)"
                    @click="handleCancel"
                >Cancel</button>

                <button
                    type="button"
                    data-test="seat-remove-confirm"
                    class="inline-flex items-center rounded-[50px] px-[20px] py-[10px]
                        border border-(--magenta) text-(--magenta)
                        font-hubspot-serif text-[17px] leading-[1.4] tracking-[0.34px] cursor-pointer
                        transition-colors duration-200 hover:bg-(--magenta) hover:text-white
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--magenta)"
                    @click="handleConfirm"
                >{{ confirmLabel }}</button>
            </div>
        </div>

        <div class="flex justify-center mt-[25px]">
            <button
                type="button"
                data-test="seat-remove-close"
                class="bg-transparent border-none p-0 cursor-pointer
                    font-hubspot-sans font-light text-[14px] leading-[1.5] tracking-[0.28px]
                    text-(--darker-pink) underline decoration-dotted decoration-[8.5%] underline-offset-[3px]
                    transition-colors duration-200 hover:text-(--purple) hover:no-underline"
                @click="open = false"
            >Close</button>
        </div>
    </FragmentsModalShell>
</template>
